// src/api/apiClient.js

import {
  getAccessToken,
  updateAccessToken,
  clearAuthData
} from '../utils/tokenStorage';

// Base URL from environment variables
const BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Flag to prevent infinite refresh token loops
let isRefreshing = false;
let failedQueue = [];

// Process queue of failed requests
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Utility function to safely stringify data for API requests
const safeStringify = (data, url) => {
  try {
    // Use a replacer function to handle circular references
    const seen = new WeakSet();
    return JSON.stringify(data, (key, value) => {
      // Skip DOM nodes, React elements, and circular references
      if (key === 'window' || key === 'document' || key === 'global' ||
        (typeof value === 'object' && value !== null)) {
        // Check for circular references
        if (seen.has(value)) {
          return undefined; // Skip circular reference
        }

        // Check for DOM nodes and React elements
        if (value && (value.nodeType !== undefined ||
          value._reactInternalFiber !== undefined ||
          value._reactInternals !== undefined ||
          key.startsWith('__react') ||
          (typeof HTMLElement === 'function' && value instanceof HTMLElement))) {
          return undefined; // Skip DOM/React elements
        }

        // Add object to seen set to detect circular references
        if (typeof value === 'object' && value !== null) {
          seen.add(value);
        }
      }
      return value;
    });
  } catch (error) {
    console.error('Error stringifying data for API request:', error);
    // Create a minimal safe version with primitive values only
    return JSON.stringify({
      error: 'Data contained circular references and could not be serialized',
      url: url
    });
  }
};

// Token refresh functionality — refresh token is sent via HttpOnly cookie
// Exported for SocketContext handshake retry (AUTH_EXPIRED path)
export const refreshAccessToken = async () => {
  try {
    console.log('Attempting to refresh access token...');
    const response = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error('Refresh token expired or invalid');
    }

    const data = await response.json();

    if (data.success && data.data?.accessToken) {
      console.log('Access token refreshed successfully');
      updateAccessToken(data.data.accessToken);
      return data.data.accessToken;
    } else {
      throw new Error('Invalid refresh response');
    }
  } catch (error) {
    console.error('Token refresh failed:', error.message);
    clearAuthData();
    window.dispatchEvent(new CustomEvent('auth:sessionExpired', {
      detail: {
        message: 'Votre session a expiré. Veuillez vous reconnecter.',
        currentPath: window.location.pathname
      }
    }));
    throw error;
  }
};

// Generic fetch wrapper with token handling
const fetchWithAuth = async (url, options = {}) => {
  // Always use silent auth for authentication-related endpoints to prevent page reloads
  const isAuthEndpoint = url.includes('/auth/');

  if (isAuthEndpoint) {
    // Force silent auth for all auth endpoints
    options.silentAuth = true;
  }

  // Create headers with authentication if token exists.
  // FormData bodies must NOT get a JSON Content-Type: the browser sets the
  // multipart boundary automatically, and forcing application/json makes
  // Express's json parser choke on the boundary ("Unexpected token '-'").
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const token = getAccessToken();
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...options.headers
  };
  if (isFormData) delete headers['Content-Type'];

  if (token) {
    // Make sure the token is properly formatted
    headers['Authorization'] = `Bearer ${token.trim()}`;
  }

  // DEBUG LOGGING - Trace what we're sending
  console.log(`[API_DEBUG] ${new Date().toISOString()} - Making request:`, {
    fullUrl: `${BASE_URL}${url}`,
    method: options.method || 'GET',
    hasToken: !!token,
    tokenPreview: token ? `${token.substring(0, 20)}...` : 'NO TOKEN',
    headers: Object.keys(headers)
  });

  // Make the request
  try {
    const response = await fetch(`${BASE_URL}${url}`, {
      ...options,
      headers,
      credentials: 'include'
    });

    // DEBUG LOGGING - Response info
    console.log(`[API_DEBUG] ${new Date().toISOString()} - Response received:`, {
      url: url,
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    });

    // If the response is OK, return it
    if (response.ok) {
      // Capture rate limit headers
      const rateLimitInfo = {
        remaining: response.headers.get('RateLimit-Remaining'),
        limit: response.headers.get('RateLimit-Limit'),
        reset: response.headers.get('RateLimit-Reset')
      };

      if (response.status === 204) {
        return { success: true, rateLimit: rateLimitInfo };
      }

      // For blob responses, return the blob directly
      if (options.responseType === 'blob') {
        return response.blob();
      }

      // Otherwise parse JSON
      const jsonResponse = await response.json();
      // Add rate limit info to response
      jsonResponse.rateLimit = rateLimitInfo;
      return jsonResponse;
    }

    // Handle 401 Unauthorized - attempt token refresh first
    if (response.status === 401) {
      console.log('Unauthorized request - attempting token refresh');

      // Don't try to refresh if this is already a refresh request
      if (url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/mfa/confirm')) {
        // Don't clear auth data for mfa/confirm — it's a wrong code, not expired session
        if (url.includes('/auth/mfa/confirm')) {
          throw new Error('MFA verification failed');
        }
        clearAuthData();
        throw new Error('Authentication failed - please log in again');
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(newToken => {
          // Retry the original request with new token
          options.headers = {
            ...options.headers,
            'Authorization': `Bearer ${newToken}`
          };
          return fetchWithAuth(url, options);
        });
      }

      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        isRefreshing = false;
        processQueue(null, newToken);

        // Retry the original request with new token
        options.headers = {
          ...options.headers,
          'Authorization': `Bearer ${newToken}`
        };
        return fetchWithAuth(url, options);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError, null);

        // Clear auth data and notify
        clearAuthData();

        if (window.location.pathname !== '/login' && !options.silentAuth) {
          window.dispatchEvent(new CustomEvent('auth:sessionExpired', {
            detail: {
              message: 'Votre session a expiré. Veuillez vous reconnecter.',
              currentPath: window.location.pathname
            }
          }));
        }

        throw new Error('Authentication failed - please log in again');
      }
    }

    // For other errors, parse response and throw
    let errorData;
    try {
      errorData = await response.json();
    } catch (e) {
      errorData = { message: response.statusText };
    }

    // Capture rate limit headers for error responses too
    const rateLimitInfo = {
      remaining: response.headers.get('RateLimit-Remaining'),
      limit: response.headers.get('RateLimit-Limit'),
      reset: response.headers.get('RateLimit-Reset')
    };

    const error = {
      status: response.status,
      data: errorData,
      message: errorData.message || 'Request failed',
      rateLimit: rateLimitInfo,
      isRateLimited: response.status === 429
    };

    // Suppress 404 errors for CTI endpoints (expected when no data exists)
    const isCTIEndpoint = url.includes('/cti-results');
    const is404Error = response.status === 404;
    const is403Error = response.status === 403;

    if (is403Error) {
      // Special detailed logging for 403 permission errors
      console.error(`[API_DEBUG] 🔴 403 FORBIDDEN ERROR:`, {
        url: url,
        fullUrl: `${BASE_URL}${url}`,
        errorMessage: errorData.message,
        errorData: errorData,
        timestamp: new Date().toISOString(),
        hint: 'Check backend logs for [ACCESS_CONTROL] entries to see why access was denied'
      });
    } else if (isCTIEndpoint && is404Error) {
      // Log 404 CTI errors as info instead of error (this is expected behavior)
      console.log(`CTI data not found (404) - this is normal when no analysis has been performed yet`);
    } else {
      // Log other errors normally
      console.error(`Error response from API endpoint`, {
        url: url,
        status: response.status,
        message: errorData.message
      });
    }

    throw error;
  } catch (error) {
    // Log without revealing sensitive data
    console.error(`Network error for API endpoint`);
    throw error;
  }
};

// API client methods
export const api = {
  /**
   * GET request
   * @param {string} url - API endpoint
   * @param {Object} params - Query parameters
   * @param {Object} options - Additional options
   * @param {boolean} options.silentAuth - Whether to handle auth silently (no page reload)
   * @returns {Promise} - Response promise
   */
  get: (url, params = {}, options = {}) => {
    // Add query params to URL
    const queryString = new URLSearchParams(params).toString();
    const urlWithParams = queryString ? `${url}?${queryString}` : url;

    return fetchWithAuth(urlWithParams, {
      method: 'GET',
      silentAuth: options.silentAuth
    });
  },

  /**
   * POST request
   * @param {string} url - API endpoint
   * @param {Object} data - Request body
   * @param {Object} options - Additional options
   * @param {boolean} options.silentAuth - Whether to handle auth silently (no page reload)
   * @returns {Promise} - Response promise
   */
  post: async (url, data = {}, options = {}) => {
    // Use the utility function to safely stringify the data
    const safeData = safeStringify(data, url);

    const response = await fetchWithAuth(url, {
      method: 'POST',
      body: safeData,
      silentAuth: options.silentAuth
    });

    // Si c'est un succès (que ce n'est pas un endpoint d'auth), on émet l'événement de sauvegarde
    if (response && !url.includes('/auth/')) {
      window.dispatchEvent(new CustomEvent('ebiosrm_manual_save'));
    }

    return response;
  },

  /**
   * PUT request
   * @param {string} url - API endpoint
   * @param {Object} data - Request body
   * @param {Object} options - Additional options
   * @param {boolean} options.silentAuth - Whether to handle auth silently (no page reload)
   * @returns {Promise} - Response promise
   */
  put: async (url, data = {}, options = {}) => {
    // Use the utility function to safely stringify the data
    const safeData = safeStringify(data, url);

    const response = await fetchWithAuth(url, {
      method: 'PUT',
      body: safeData,
      silentAuth: options.silentAuth
    });

    // Si c'est un succès (que ce n'est pas un endpoint d'auth), on émet l'événement de sauvegarde
    if (response && !url.includes('/auth/')) {
      window.dispatchEvent(new CustomEvent('ebiosrm_manual_save'));
    }

    return response;
  },

  /**
   * PATCH request
   * @param {string} url - API endpoint
   * @param {Object} data - Request body
   * @param {Object} options - Additional options
   * @param {boolean} options.silentAuth - Whether to handle auth silently (no page reload)
   * @returns {Promise} - Response promise
   */
  patch: async (url, data = {}, options = {}) => {
    const safeData = safeStringify(data, url);
    const response = await fetchWithAuth(url, {
      method: 'PATCH',
      body: safeData,
      silentAuth: options.silentAuth
    });
    if (response && !url.includes('/auth/')) {
      window.dispatchEvent(new CustomEvent('ebiosrm_manual_save'));
    }
    return response;
  },

  /**
   * DELETE request
   * @param {string} url - API endpoint
   * @param {Object} params - Query parameters
   * @param {Object} options - Additional options
   * @param {boolean} options.silentAuth - Whether to handle auth silently (no page reload)
   * @returns {Promise} - Response promise
   */
  delete: (url, params = {}, options = {}) => {
    // Add query params to URL
    const queryString = new URLSearchParams(params).toString();
    const urlWithParams = queryString ? `${url}?${queryString}` : url;

    return fetchWithAuth(urlWithParams, {
      method: 'DELETE',
      silentAuth: options.silentAuth
    });
  },

  /**
   * Download a file
   * @param {string} url - API endpoint
   * @param {Object} params - Query parameters
   * @param {Object} options - Additional options
   * @param {boolean} options.silentAuth - Whether to handle auth silently (no page reload)
   * @returns {Promise<Blob>} - Blob promise
   */
  download: (url, params = {}, options = {}) => {
    // Add query params to URL
    const queryString = new URLSearchParams(params).toString();
    const urlWithParams = queryString ? `${url}?${queryString}` : url;

    return fetchWithAuth(urlWithParams, {
      method: 'GET',
      responseType: 'blob',
      silentAuth: options.silentAuth
    });
  },

  /**
   * Upload file(s)
   * @param {string} url - API endpoint
   * @param {FormData} formData - Form data with files
   * @param {Function} onProgress - Progress callback (not supported in fetch)
   * @param {Object} options - Additional options
   * @param {boolean} options.silentAuth - Whether to handle auth silently (no page reload)
   * @returns {Promise} - Response promise
   */
  upload: (url, formData, onProgress = null, options = {}) => {
    // Note: fetch doesn't support upload progress
    if (onProgress) {
      // Removed sensitive data logging for security
    }

    // Don't set Content-Type header, let the boundary be set automatically
    return fetchWithAuth(url, {
      method: 'POST',
      headers: {}, // Override the default Content-Type
      body: formData,
      silentAuth: options?.silentAuth
    });
  }
};

export default api;